import React from 'react';

import { Alert, Box } from '@mui/material';

import { Button } from 'src/components/common';
import { GRID_ROW_ERROR_CLASSNAME, Table, TableColumn } from 'src/components/table';
import { usePagination } from 'src/hooks/common';
import type { BulkUploadExtendedEntity } from 'src/types/bulkUpload';
import { getPaginatedData } from 'src/utils/pagination';

type BulkUploadTableProps<TRecord extends BulkUploadExtendedEntity = BulkUploadExtendedEntity> = {
  entitites: TRecord[];
  columns: TableColumn<TRecord>[];
  invalidEntititesMessage: React.ReactNode;
  loading: boolean;
  handleSaveChanges: () => Promise<void>;
  handleBack: () => void;
};

export const BulkUploadTable = <TRecord extends BulkUploadExtendedEntity>({
  entitites,
  columns,
  invalidEntititesMessage,
  loading,
  handleSaveChanges,
  handleBack,
}: BulkUploadTableProps<TRecord>): React.ReactElement => {
  const pagination = usePagination();

  const paginatedEmployers = React.useMemo(() => {
    return getPaginatedData(entitites, pagination);
  }, [entitites, pagination]);

  const isSomeEmployersInvalid = React.useMemo(() => {
    return entitites.length > 0 && entitites.some(employer => !employer.valid);
  }, [entitites]);

  const isAllEmployersInvalid = React.useMemo(() => {
    return entitites.length > 0 && entitites.every(employer => !employer.valid);
  }, [entitites]);

  return (
    <Box>
      {isSomeEmployersInvalid && <Alert severity="error">{invalidEntititesMessage}</Alert>}

      <Box mt={2}>
        <Table
          dataSource={paginatedEmployers}
          rowCount={entitites.length}
          columns={columns}
          pagination={pagination}
          loading={loading}
          disabled={loading}
          getRowClassName={params => (params.row.valid ? '' : GRID_ROW_ERROR_CLASSNAME)}
          hideTableTopMenu
        />
      </Box>

      <Box display="flex" justifyContent="center" mt={4} gap={4}>
        <Button onClick={handleBack} disabled={loading} color="error" variant="outlined">
          Back
        </Button>

        <Button
          onClick={handleSaveChanges}
          loading={loading}
          disabled={loading || isAllEmployersInvalid}
          variant="contained"
          color="primary"
        >
          Save Changes
        </Button>
      </Box>
    </Box>
  );
};
